import { Chip, makeStyles } from '@material-ui/core';
import { CustomizedTypography, Stack, VStack } from '@components/Customized';
import { colorSet, clsx } from '@components/Provider';
import * as Assets from '@assets';
import {
  CapaQuotationServiceType,
  CapaQuotationUserChatRoom
} from '@generated/graphql';
import AppConfig from '@config';
import { formatDate, requestEnumToString } from '@utils';
import { Image } from '@components/Image';
import { CallBackRef } from '@hooks/useIntersectionObserver';
import { memo } from 'react';
import { useHistory } from 'react-router-dom';
import useDashboard from '@hooks/useDashboard';
import { ProjectStatusLabel } from '../RequestList/RfqCard/RfqCardStatus';

interface MyChatRoomProps {
  route: 'dashboard' | 'chat';
  isOldRfq: boolean;
  isSelected: boolean;
  isLast: boolean;
  room?: CapaQuotationUserChatRoom;
  refetch: () => void;
  callbackRef: CallBackRef<HTMLDivElement>;
  onChangeVisible: (_value: boolean) => void;
}

const MyChatCard = ({
  room,
  isLast,
  isOldRfq,
  isSelected,
  refetch,
  callbackRef,
  onChangeVisible
}: MyChatRoomProps) => {
  const classes = useMyChatCardStyles();
  const history = useHistory();
  const serviceType = room?.serviceType;
  const avatar = `${AppConfig.fileApiUrl}/file/${room?.profileId}`;
  const thumbnail =
    serviceType === CapaQuotationServiceType.Design
      ? `${AppConfig.fileApiUrl}/file/${room?.thumbnailId}`
      : `${AppConfig.fileApiUrl}/cloud/${room?.thumbnailId}.png`;

  const isPcbDesign =
    serviceType === CapaQuotationServiceType.Pcb && !room?.thumbnailId;
  const isWithdrawnPartner = room?.quotationPartnerStatus === 'DELETED';

  const { onUpdateSelectedQuotation } = useDashboard();

  const onClick = () => {
    if (room?.unReadCount !== 0) {
      refetch();
    }

    onUpdateSelectedQuotation({
      rfqId: room?.rfqId ?? '',
      chatRoomId: room?.chatRoomId ?? '',
      partnerId: room?.quotationPartnerId ?? '',
      isWithdrawn: isWithdrawnPartner
    });
    onChangeVisible(true);

    return history.push(`/chat/${room?.chatRoomId}`);
  };

  return (
    <div
      ref={(instance) => {
        if (isLast) {
          return callbackRef(instance);
        }
      }}
      className={clsx(classes.root, isSelected ? 'isSelected' : '')}
      onClick={onClick}
    >
      <ProjectStatusLabel status={room?.searchProjectStatusTypeEnum} />

      <Stack className={classes.projectInfo}>
        <div className={classes.thumbnailBox}>
          <div>
            {!isPcbDesign ? (
              <Image
                src={room?.thumbnailId ? thumbnail : Assets.DefaultThumbnail}
                onError={(e) => {
                  e.currentTarget.src = Assets.DefaultThumbnail;
                }}
                alt=""
                className={classes.thumbnail}
              />
            ) : (
              <div className="pcb-design-icon">
                <img src={Assets.PCBDesignIcon} alt="PCB 설계" />
              </div>
            )}
          </div>
          <div className={classes.avatarBox}>
            {room?.unReadCount !== 0 && (
              <Chip label={room?.unReadCount} className={classes.newCount} />
            )}
            <Image
              className={classes.avatar}
              src={room?.profileId ? avatar : Assets.DefaultProfile}
              alt=""
              onError={(e) => {
                e.currentTarget.src = Assets.DefaultProfile;
              }}
            />
          </div>
        </div>

        <div className={classes.info}>
          <CustomizedTypography
            className={classes.label}
            variant="body1"
            fontWeight="500"
            gutterBottoms={2}
          >
            {room?.projectName}
          </CustomizedTypography>

          <CustomizedTypography variant="body2" color="textSecondary">
            요청서 작성일 {formatDate(room?.rfqCreatedAt ?? '', 'YYYY.MM.DD')}
          </CustomizedTypography>
          <CustomizedTypography
            variant="body2"
            color="textSecondary"
            gutterBottoms={8}
          >
            {requestEnumToString(room?.serviceType ?? undefined)}{' '}
            {room?.quantity ? (
              <>
                | 수량{' '}
                <span className="quantity">
                  {Number(room.quantity).toLocaleString()}
                </span>
                개
              </>
            ) : null}
          </CustomizedTypography>

          <VStack className={classes.partnerInfoBox}>
            <CustomizedTypography variant="subtitle2" fontWeight="500">
              {room?.managerName} ({room?.organizationName})
            </CustomizedTypography>
            <CustomizedTypography variant="subtitle2" color="textSecondary">
              {room?.shortAddress}
            </CustomizedTypography>
          </VStack>
        </div>
      </Stack>

      <CustomizedTypography className={classes.createdAt}>
        {formatDate(room?.rfqCreatedAt ?? new Date().toString(), 'YYYY-MM-DD')}
      </CustomizedTypography>
    </div>
  );
};

export default memo(MyChatCard);
const useMyChatCardStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '20px 16px',
    maxWidth: 375,
    borderTop: `1px solid ${colorSet.gray200}`,
    position: 'relative',
    cursor: 'pointer',
    gap: '8px',
    '& .pcb-design-icon': {
      height: 110,
      width: 100,
      minHeight: 110,
      minWidth: 100,
      borderRadius: 8,
      border: `1px solid ${colorSet.gray200}`,
      bottom: 17,
      right: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& img': {
        width: 70
      }
    },
    '&.isSelected': {
      backgroundColor: colorSet.gray200
    },
    '&:hover': {
      backgroundColor: colorSet.gray200
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      width: '100%'
    }
  },
  projectInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%'
  },
  thumbnailBox: {
    width: '100%',
    maxWidth: 112,
    height: 126,
    position: 'relative',
    '& > div:first-child': {
      width: 100,
      height: 110,
      borderRadius: '16px',
      backgroundColor: colorSet.gray000
    }
  },
  thumbnail: {
    border: `1px solid ${colorSet.gray200}`,
    borderRadius: 16,
    width: 109,
    height: 109
  },
  avatarBox: {
    height: 52,
    width: 52,
    borderRadius: 50,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    bottom: 36,
    left: 64,
    overflow: 'visible'
  },
  avatar: {
    height: 44,
    width: 44,
    maxWidth: 44,
    maxHeight: 44,
    borderRadius: 50,
    boxShadow: '0px 16px 32px 2px #6161611A, 0px 8px 16px 2px #6161611A',
    background: colorSet.gray000
  },
  partnerInfoBox: {
    background: colorSet.gray100,
    padding: '8px 12px',
    borderRadius: '8px',
    width: '100%',
    minWidth: 0,
    '& > p, h6': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%'
    }
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    minWidth: 0,
    '& .quantity': {
      color: colorSet.primary500
    },
    '& > p': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  },
  label: {
    maxWidth: 168,
    height: 24,
    whiteSpace: 'pre',
    wordBreak: 'break-all',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&.message': {
      maxWidth: 150,
      height: 20
    }
  },
  newCount: {
    width: 18,
    height: 18,
    position: 'absolute',
    left: 28,
    top: -6,
    backgroundColor: colorSet.errorMain,
    border: `1px solid ${colorSet.gray000}`,
    '& span': {
      fontSize: 10,
      lineHeight: 16,
      color: colorSet.gray000,
      padding: '1px 2px'
    }
  },
  createdAt: {
    fontSize: '12px',
    lineHeight: '18px',
    color: colorSet.gray500,
    position: 'absolute',
    top: 19,
    right: 16
  }
}));
